import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { PROVEDORES_DE_IA, provedorDeIa } from '@/data/integracoes';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import type { AppDaMetaDaPlataforma, ChaveDeIa, GatewayDePagamento, ProvedorDeCanalDaPlataforma, ProvedorDeIa } from '@/data/tipos';
import { AppDaMeta } from './AppDaMeta';
import { GatewaysDePagamento } from './GatewaysDePagamento';

export interface CargaDasIntegracoesDaPlataforma {
    chaves: ChaveDeIa[];
    canais: ProvedorDeCanalDaPlataforma[];
    gateways: GatewayDePagamento[];
    meta: AppDaMetaDaPlataforma | null;
}

export async function carregarChavesDeIa(): Promise<CargaDasIntegracoesDaPlataforma> {
    const [chaves, canais, gateways, meta] = await Promise.all([
        dados().chavesDeIa(),
        dados().provedoresDeCanalDaPlataforma(),
        dados().gatewaysDePagamento(),
        dados().appDaMeta(),
    ]);
    return { chaves, canais, gateways, meta };
}

/**
 * As chaves de IA da plataforma.
 *
 * São da PLATAFORMA, e não de cada clínica: é a conta da VecchIA que paga a
 * IA, e cobrar cada clínica para trazer a própria chave transformaria um
 * recurso do produto numa tarefa de configuração. Por isso esta tela existe
 * aqui e não em Configurações.
 *
 * A chave entra e não volta — o mesmo desenho das credenciais de canal. O que
 * se lê de volta são os quatro últimos caracteres: bastam para reconhecer qual
 * chave está lá e não bastam para usá-la.
 */
export function IntegracoesDaPlataforma() {
    const { chaves, canais, gateways, meta } = useLoaderData() as CargaDasIntegracoesDaPlataforma;
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [emEdicao, setEmEdicao] = useState<ProvedorDeIa | null>(null);
    const [chave, setChave] = useState('');
    const [modelo, setModelo] = useState('');
    const [salvando, setSalvando] = useState(false);

    /* O servidor de WhatsApp é da plataforma pela mesma razão que a chave de IA:
       é a VecchIA que contrata o servidor UAZAPI, e o token de administração
       dele comanda todas as instâncias — as desta clínica e as das outras. */
    const [servidorAberto, setServidorAberto] = useState(false);
    const [servidorUrl, setServidorUrl] = useState('');
    const [servidorToken, setServidorToken] = useState('');
    const servidorSujo = useAlteracaoPendente({ servidorUrl, servidorToken }, servidorAberto);
    const chaveSuja = useAlteracaoPendente({ chave, modelo }, emEdicao);
    const [confirmar, confirmacao] = useConfirmacao();

    const uazapi = canais.find((c) => c.provedor === 'uazapi');

    const abrir = (provedor: ProvedorDeIa) => {
        const atual = chaves.find((c) => c.provedor === provedor);
        setEmEdicao(provedor);
        /* Vazio sempre: não há chave para trazer de volta. */
        setChave('');
        setModelo(atual?.modelo ?? provedorDeIa(provedor)?.padrao ?? '');
    };

    const salvar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            await dados().salvarChaveDeIa(emEdicao, chave, modelo || undefined);
            aviso({ title: 'Chave guardada', description: `${provedorDeIa(emEdicao)?.nome} · cifrada no cofre` });
            setEmEdicao(null);
            setChave('');
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível guardar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const remover = async (provedor: ProvedorDeIa) => {
        const ok = await confirmar({
            titulo: `Remover a chave da ${provedorDeIa(provedor)?.nome ?? provedor}?`,
            descricao: 'Os agentes, os fluxos e a base de conhecimento param de usar este provedor em todas as clínicas.',
            confirmar: 'Remover chave',
        });
        if (!ok) return;
        try {
            await dados().removerChaveDeIa(provedor);
            aviso({ title: 'Chave removida', description: `${provedorDeIa(provedor)?.nome} · a IA para de usar este provedor` });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const abrirServidor = () => {
        setServidorAberto(true);
        setServidorUrl(uazapi?.baseUrl ?? 'https://');
        /* Vazio sempre: o token não volta, e é isso que este desenho garante. */
        setServidorToken('');
    };

    const salvarServidor = async () => {
        setSalvando(true);
        try {
            await dados().salvarProvedorDeCanal('uazapi', servidorUrl, servidorToken);
            aviso({ title: 'Servidor guardado', description: 'UAZAPI · token cifrado no cofre' });
            setServidorAberto(false);
            setServidorToken('');
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível guardar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const removerServidor = async () => {
        const ok = await confirmar({
            titulo: 'Remover o servidor UAZAPI?',
            descricao: 'As clínicas param de conseguir parear novos números. As instâncias já pareadas continuam lá.',
            confirmar: 'Remover servidor',
        });
        if (!ok) return;
        try {
            await dados().removerProvedorDeCanal('uazapi');
            aviso({
                title: 'Servidor removido',
                description: 'As clínicas param de conseguir parear novos números. As instâncias já pareadas continuam lá.',
            });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const descricao = emEdicao ? provedorDeIa(emEdicao) : undefined;
    const algumaChave = chaves.some((c) => c.temChave && c.ativo);

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Integrações e IA"
                description="As chaves que os agentes, os fluxos e a base de conhecimento usam. São da plataforma — a clínica não traz a própria."
            />

            {!algumaChave && (
                <Alert tone="warning" dismissible={false} title="A IA está desligada">
                    Sem nenhuma chave configurada, os agentes, os fluxos e a base de conhecimento não respondem em
                    nenhuma clínica. Guardar uma chave abaixo liga a IA para toda a base.
                </Alert>
            )}

            <Alert tone="info" dismissible={false} title="A chave entra e não volta">
                Ela é cifrada no cofre do banco e lida só pela função da borda, que é quem fala com o provedor.
                O navegador nunca a recebe — se ele a recebesse, qualquer pessoa com o aplicativo aberto teria
                a chave da conta.
            </Alert>

            <Card
                title="Servidor de WhatsApp"
                subtitle="É ele que cria as instâncias quando uma clínica gera o QR Code"
                padding={18}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Icon name="message-circle" size={16} />
                        <strong style={{ fontSize: 14, fontWeight: 600 }}>UAZAPI</strong>
                        <Badge tone={uazapi?.temToken ? 'success' : 'neutral'} dot>
                            {uazapi?.temToken ? 'Configurado' : 'Sem servidor'}
                        </Badge>
                    </div>

                    <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                        {uazapi?.temToken ? (
                            <>
                                <code>{uazapi.baseUrl}</code> · token terminado em <code>{uazapi.dica}</code>
                                <br />
                                Atualizado em {new Date(uazapi.atualizadoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })}
                            </>
                        ) : (
                            <>
                                Sem ele, a clínica não vê o botão de gerar QR Code e precisa colar instância e
                                token à mão, criados por fora.
                            </>
                        )}
                    </p>

                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <Button size="sm" variant="secondary" onClick={abrirServidor}>
                            {uazapi?.temToken ? 'Trocar servidor' : 'Configurar'}
                        </Button>
                        {uazapi?.temToken && (
                            <Button size="sm" variant="ghost" onClick={removerServidor}>Remover</Button>
                        )}
                    </span>
                </div>
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(320px,100%),1fr))', gap: 12 }}>
                {PROVEDORES_DE_IA.map((p) => {
                    const atual = chaves.find((c) => c.provedor === p.id);
                    return (
                        <Card key={p.id} padding={18}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Icon name="sparkles" size={16} />
                                    <strong style={{ fontSize: 14, fontWeight: 600 }}>{p.nome}</strong>
                                    <Badge tone={atual?.temChave ? 'success' : 'neutral'} dot>
                                        {atual?.temChave ? 'Configurada' : 'Sem chave'}
                                    </Badge>
                                </div>

                                <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                                    {atual?.temChave ? (
                                        <>
                                            Chave terminada em <code>{atual.dica}</code>
                                            {atual.modelo ? <> · modelo <code>{atual.modelo}</code></> : null}
                                            <br />
                                            Atualizada em {new Date(atual.atualizadoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })}
                                        </>
                                    ) : (
                                        <>
                                            Modelo padrão sugerido: <code>{p.padrao}</code>
                                            <br />
                                            <a href={p.documentacao} target="_blank" rel="noreferrer" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                                                Onde gerar a chave
                                            </a>
                                        </>
                                    )}
                                </p>

                                <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    <Button size="sm" variant="secondary" onClick={() => abrir(p.id)}>
                                        {atual?.temChave ? 'Trocar chave' : 'Configurar'}
                                    </Button>
                                    {atual?.temChave && (
                                        <Button size="sm" variant="ghost" onClick={() => remover(p.id)}>Remover</Button>
                                    )}
                                </span>
                            </div>
                        </Card>
                    );
                })}
            </div>

            <GatewaysDePagamento gateways={gateways} />

            <AppDaMeta app={meta} />

            {confirmacao}

            <Modal
                open={servidorAberto}
                title="Servidor UAZAPI"
                onClose={() => setServidorAberto(false)}
                dirty={servidorSujo}
                width={560}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setServidorAberto(false)}>Cancelar</Button>
                        <Button
                            onClick={salvarServidor}
                            disabled={salvando || servidorToken.trim().length < 16 || !servidorUrl.startsWith('https://')}
                        >
                            {salvando ? 'Guardando…' : 'Guardar servidor'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Alert tone="warning" dismissible={false} title="Este token comanda o servidor inteiro">
                        Com ele se cria, lista e apaga <strong>qualquer instância</strong> daquele servidor,
                        inclusive as de outros clientes hospedados nele. Por isso ele fica no cofre e só a
                        função da borda o lê: o navegador nunca o recebe, nem uma vez.
                    </Alert>

                    <FormField
                        label="Endereço do servidor" htmlFor="uaz-url" required
                        hint="Precisa ser https:// — sem TLS o token viajaria em claro."
                    >
                        <Input id="uaz-url" value={servidorUrl} onChange={setServidorUrl} placeholder="https://seu-servidor.uazapi.com" />
                    </FormField>

                    <FormField label="Token de administração" htmlFor="uaz-token" required>
                        <Input id="uaz-token" type="password" autoComplete="off" value={servidorToken} onChange={setServidorToken} />
                    </FormField>

                    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        A Fórmula MED <strong>nunca apaga</strong> instância: desconectar encerra a sessão do
                        WhatsApp e a instância continua no servidor, pronta para outro QR.
                    </p>
                </div>
            </Modal>

            <Modal
                open={emEdicao !== null}
                title={descricao ? `Chave da ${descricao.nome}` : 'Chave de IA'}
                onClose={() => setEmEdicao(null)}
                dirty={chaveSuja}
                width={560}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={salvar} disabled={chave.trim().length < 16 || salvando}>
                            {salvando ? 'Guardando…' : 'Guardar chave'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <FormField
                        label="Chave de API" htmlFor="ia-chave" required
                        hint={descricao?.prefixo ? `Costuma começar com ${descricao.prefixo}` : undefined}
                    >
                        <Input id="ia-chave" type="password" autoComplete="off" value={chave} onChange={setChave} />
                    </FormField>

                    <FormField label="Modelo padrão" htmlFor="ia-modelo" hint="A IA usa este quando o agente não pede outro.">
                        <Select
                            id="ia-modelo" value={modelo} onChange={setModelo}
                            options={(descricao?.modelos ?? []).map((m) => ({ value: m, label: m }))}
                            placeholder="Escolha o modelo"
                        />
                    </FormField>

                    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        A chave vale para <strong>todas as clínicas</strong>. O consumo é registrado por clínica,
                        e aparece em Consumo de IA.
                    </p>
                </div>
            </Modal>
        </div>
    );
}
